import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box, Stack, Paper, Typography, Chip, Button, TextField, MenuItem, Divider, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import Iconify from "../../../components/color-utils/Iconify";
import {
  getStatusOptions, createStatusOption, updateStatusOption, deleteStatusOption,
} from "../../../redux/features/careerSlice";

// who a status is for
const AUDIENCES = [
  { value: "officer", label: "Officer" },
  { value: "student", label: "Student" },
  { value: "both", label: "Both" },
];
const audienceLabel = (a) => AUDIENCES.find((x) => x.value === a)?.label || "Both";

// Manage the application statuses officers can move applicants through.
// Table view with search, inline edit, remove (soft-delete), and an "audience"
// (who the status is for: officer / student / both).
const CareerStatuses = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { statusOptions, statusLoading } = useSelector((s) => s.career);

  // add-a-status box
  const [newName, setNewName] = useState("");
  const [newAudience, setNewAudience] = useState("officer");
  // inline row edit
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAudience, setEditAudience] = useState("officer");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => dispatch(getStatusOptions());
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = (s) => {
    setEditingId(s.career_application_status_id);
    setEditName(s.status_label);
    setEditAudience(s.audience || "both");
  };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };

  const add = () => {
    if (!newName.trim()) return enqueueSnackbar("Status name required", { variant: "warning" });
    setBusy(true);
    dispatch(createStatusOption({ status_label: newName.trim(), audience: newAudience }))
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar("Status added", { variant: "success" });
          setNewName("");
          load();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const saveEdit = () => {
    if (!editName.trim()) return enqueueSnackbar("Status name required", { variant: "warning" });
    setBusy(true);
    dispatch(updateStatusOption({ id: editingId, status_label: editName.trim(), audience: editAudience }))
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar("Status updated", { variant: "success" });
          cancelEdit();
          load();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  const remove = (s) => {
    setBusy(true);
    dispatch(deleteStatusOption(s.career_application_status_id))
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar("Status removed", { variant: "success" });
          if (s.career_application_status_id === editingId) cancelEdit();
          load();
        } else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setBusy(false));
  };

  // client-side search by status name
  const q = search.trim().toLowerCase();
  const rows = (statusOptions || []).filter((s) => !q || s.status_label.toLowerCase().includes(q));

  return (
    <GlobalWrapper title={"Application Statuses"}>
      <Box sx={{ maxWidth: 820 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Statuses an application can be in. Pick who each one is for: Officer (officer pipeline), Student (student-facing), or Both. Click Edit to change one in place; Remove hides it (it can be added again later).
        </Typography>

        {/* search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search statuses"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-outline" />
              </InputAdornment>
            ),
          }}
        />

        {/* table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ "& .MuiTableCell-root": { px: { xs: 0.75, sm: 2 } } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }}>Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ width: { xs: 84, sm: 150 } }}>For whom</TableCell>
                <TableCell align="right" sx={{ width: { xs: 90, sm: 190 } }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {statusLoading ? "Loading…" : q ? "No statuses match your search." : "No statuses yet."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((s) => {
                const isEditing = s.career_application_status_id === editingId;
                return (
                  <TableRow key={s.career_application_status_id} hover selected={isEditing}>
                    <TableCell>{s.sort_order}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          fullWidth
                          autoFocus
                          value={editName}
                          inputProps={{ maxLength: 120 }}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={600}>{s.status_label}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={editAudience}
                          onChange={(e) => setEditAudience(e.target.value)}
                        >
                          {AUDIENCES.map((a) => (
                            <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <Chip size="small" variant="outlined" label={audienceLabel(s.audience)} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        sx={{ flexWrap: "wrap", gap: 1 }}
                      >
                        {isEditing ? (
                          <>
                            <Button size="small" variant="contained" disabled={busy} onClick={saveEdit}>
                              {busy ? "Saving…" : "Save"}
                            </Button>
                            <Button size="small" variant="outlined" disabled={busy} onClick={cancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <>
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => startEdit(s)}>Edit</Button>
                            <Button size="small" color="error" disabled={busy} onClick={() => remove(s)}>Remove</Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Add a status</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. Sent to HR"
            value={newName}
            inputProps={{ maxLength: 120 }}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
          <TextField
            select
            size="small"
            label="For whom"
            value={newAudience}
            onChange={(e) => setNewAudience(e.target.value)}
            // full width on phones so it stacks cleanly; fixed width from tablet up
            sx={{ minWidth: 150, width: { xs: "100%", sm: "auto" } }}
          >
            {AUDIENCES.map((a) => (
              <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={busy}
            onClick={add}
            sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
          >
            {busy ? "Saving…" : "Add"}
          </Button>
        </Stack>
      </Box>
    </GlobalWrapper>
  );
};

export default CareerStatuses;
